// Client portal (phone-first). Home: what is waiting on the client (returned items first, with the auditor's reason), what is
// with the auditor, and what is done. Each request, question or follow-up opens as a conversation with the firm: reply, attach
// files (through the Worker, with progress and errors in plain words), or say it does not apply with a reason. The client reads
// only client views (threads sent to them, messages sent to them, their own files); nothing firm-internal is loaded here.
import { useRef, useState, type DragEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useApp } from "../app/context";
import { Button, EmptyState, LoadState, StatusPill, Thread, inputCls } from "../components";
import type { Message, Thread as ThreadRow, ThreadAttachment, Upload } from "../data/types";
import { fmtDate, fmtStamp, plural } from "../lib/format";
import { useLoad, useRealtime, type Loaded } from "../lib/hooks";
import type { StatusKind } from "../lib/status";

const ACCEPT = ".pdf,.xlsx,.xls,.docx,.csv,.txt,.jpg,.jpeg,.png,.heic,.zip";
/** The engine's thread states in the client's words (the state itself is the engine's). */
const CLIENT_WORDS: Record<string, [StatusKind, string]> = {
  sent: ["client", "Waiting on you"], returned: ["blocked", "Please send again"], responded: ["progress", "With your auditor"],
  na_requested: ["waiting", "You said it does not apply"], accepted: ["done", "Accepted"], resolved: ["done", "Settled"], na: ["neutral", "Not needed"],
  cancelled: ["neutral", "Withdrawn"],
};
const KIND_WORDS: Record<string, string> = { request: "Documents requested", question: "Question from your auditor", followup: "Follow-up question" };
const WAITING = ["returned", "sent"];
const WITH_FIRM = ["responded", "na_requested"];
const OPEN = ["sent", "responded", "returned", "na_requested"];

export function ClientStatus({ status }: { status: string }) {
  const w = CLIENT_WORDS[status];
  return <StatusPill state={status} kind={w?.[0]} label={w?.[1]} />;
}

export function PortalPage() {
  const { ds } = useApp();
  const threads = useLoad(() => ds.clientThreads(), [ds]);
  const names = useLoad(() => ds.clientEngagements(), [ds]);
  useRealtime(ds, "threads", () => threads.reload());
  const many = (names.data?.length ?? 0) > 1;
  const name = (id: string) => names.data?.find((e) => e.id === id)?.name ?? "";
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <header>
        <h1 className="text-xl font-semibold">Your audit</h1>
        <p className="mt-1 text-sm text-muted">{names.data?.map((e) => `${e.name}${e.fye ? ` · year ended ${fmtDate(e.fye)}` : ""}`).join(" · ")}</p>
        <p className="mt-2 text-sm text-muted">Answer each item in its own conversation. Files are encrypted and go only to your audit team. An AI service helps your auditor read documents; the firm's staff check every value.</p>
      </header>
      <LoadState state={threads}>
        {(ts) => {
          const waiting = ts.filter((t) => WAITING.includes(t.status)).sort((a, b) => WAITING.indexOf(a.status) - WAITING.indexOf(b.status) || (a.due ?? "9").localeCompare(b.due ?? "9"));
          const withFirm = ts.filter((t) => WITH_FIRM.includes(t.status));
          const done = ts.filter((t) => !WAITING.includes(t.status) && !WITH_FIRM.includes(t.status));
          const card = (t: ThreadRow) => <ThreadCard key={`${t.engagementId}:${t.id}`} t={t} engagement={many ? name(t.engagementId) : ""} />;
          return (
            <>
              <section aria-labelledby="p-waiting">
                <h2 id="p-waiting" className="mb-2 text-base font-semibold">Waiting on you <span className="text-muted">({waiting.length})</span></h2>
                {waiting.length === 0 ? <EmptyState title="Nothing is waiting on you. Thank you." /> : <ul className="space-y-2">{waiting.map(card)}</ul>}
              </section>
              <section aria-labelledby="p-firm">
                <h2 id="p-firm" className="mb-2 text-base font-semibold">With your auditor <span className="text-muted">({withFirm.length})</span></h2>
                {withFirm.length === 0 ? <p className="text-sm text-muted">Nothing is with your auditor right now.</p> : <ul className="space-y-2">{withFirm.map(card)}</ul>}
              </section>
              {done.length > 0 && (
                <details className="rounded-md border border-line bg-raised p-3">
                  <summary className="cursor-pointer text-sm font-semibold">Done ({done.length})</summary>
                  <ul className="mt-2 space-y-2">{done.map(card)}</ul>
                </details>
              )}
            </>
          );
        }}
      </LoadState>
    </div>
  );
}

function ThreadCard({ t, engagement }: { t: ThreadRow; engagement: string }) {
  return (
    <li>
      <Link to={`/portal/${t.engagementId}/${t.id}`} className="block rounded-lg border border-line bg-raised p-3 hover:bg-sunken">
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted"><ClientStatus status={t.status} />{KIND_WORDS[t.kind] ?? "Message"}{t.due && ` · due ${fmtDate(t.due)}`}{engagement && ` · ${engagement}`}</span>
        <span className="mt-1 block font-medium">{t.subject}</span>
        {t.status === "returned" && t.returnReason && <span className="mt-1 block text-sm text-client">Your auditor's reason: {t.returnReason}</span>}
      </Link>
    </li>
  );
}

interface Sending { key: string; name: string; progress: number; error?: string; done?: boolean }
type Files = { ups: Upload[]; atts: ThreadAttachment[] };

export function PortalThreadPage() {
  const { engagement = "", thread = "" } = useParams();
  const { ds } = useApp();
  const t = useLoad(async () => (await ds.clientThreads()).find((x) => x.engagementId === engagement && x.id === thread) ?? null, [ds, engagement, thread]);
  const msgs = useLoad(() => ds.threadMessages(engagement, thread).catch(() => [] as Message[]), [ds, engagement, thread]);
  const files = useLoad(async (): Promise<Files> => {
    const [ups, atts] = await Promise.all([ds.clientUploads(), ds.clientAttachments()]);
    return { ups, atts };
  }, [ds]);
  const reload = () => { t.reload(); msgs.reload(); files.reload(); };
  useRealtime(ds, "threads", (e) => { if (!e.engagementId || e.engagementId === engagement) reload(); });
  return (
    <div className="mx-auto max-w-2xl space-y-3">
      <Link to="/portal" className="inline-block text-sm text-brand hover:underline">← Everything for your audit</Link>
      <LoadState state={t}>
        {(row) => !row ? <EmptyState title="This conversation is not available.">It may have been withdrawn by your auditor.</EmptyState> : <ThreadView t={row} msgs={msgs} files={files} reload={reload} />}
      </LoadState>
    </div>
  );
}

function ThreadView({ t, msgs, files, reload }: { t: ThreadRow; msgs: Loaded<Message[]>; files: Loaded<Files>; reload: () => void }) {
  const { ds } = useApp();
  const open = OPEN.includes(t.status);
  const [sending, setSending] = useState<Sending[]>([]);
  const [na, setNa] = useState(false);
  const [naText, setNaText] = useState("");
  const [naErr, setNaErr] = useState<string>();
  const [naBusy, setNaBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const mine = files.data ? [
    ...files.data.ups.filter((u) => u.engagementId === t.engagementId && t.requestId !== null && u.requestId === t.requestId).map((u) => ({ id: u.id, name: u.fileName, status: u.status, note: u.engineNote, at: u.createdAt })),
    ...files.data.atts.filter((a) => a.engagementId === t.engagementId && a.thread === t.id).map((a) => ({ id: a.id, name: a.fileName, status: a.status, note: a.engineNote, at: a.createdAt })),
  ] : [];

  const send = async (list: FileList | null) => {
    if (!list?.length) return;
    for (const f of Array.from(list)) {
      const key = `${f.name}-${f.size}-${Math.random().toString(36).slice(2)}`;
      setSending((s) => [...s, { key, name: f.name, progress: 0 }]);
      const on = (p: number) => setSending((s) => s.map((x) => (x.key === key ? { ...x, progress: p } : x)));
      try {
        if (t.kind === "request" && t.requestId !== null) await ds.upload(t.engagementId, t.requestId, f, on);
        else await ds.attach(t.engagementId, t.id, f, on);
        setSending((s) => s.map((x) => (x.key === key ? { ...x, progress: 1, done: true } : x)));
      } catch (e) {
        setSending((s) => s.map((x) => (x.key === key ? { ...x, error: plainUploadError(f.name, (e as Error).message) } : x)));
      }
    }
    if (input.current) input.current.value = "";
    reload();
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); if (open) void send(e.dataTransfer.files); };

  return (
    <article aria-label={t.subject} className="space-y-3">
      <header className="space-y-1">
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted"><ClientStatus status={t.status} />{KIND_WORDS[t.kind] ?? "Message"}{t.due && ` · due ${fmtDate(t.due)}`}</span>
        <h1 className="text-lg font-semibold">{t.subject}</h1>
      </header>
      <div className="rounded-md border border-line bg-raised px-3 py-2 text-sm">
        <span className="block text-xs text-muted">{t.kind === "request" ? "Your auditor asked for" : "Your auditor asked"}</span>
        <p className="whitespace-pre-wrap">{t.body}</p>
      </div>
      {t.status === "returned" && t.returnReason && <p role="note" className="rounded-md border border-client-line bg-client-bg px-3 py-2 text-sm text-client">Your auditor's reason: {t.returnReason}</p>}
      {t.status === "na_requested" && <p role="note" className="rounded-md border border-waiting-line bg-waiting-bg px-3 py-2 text-sm text-waiting">You said this does not apply{t.naReason ? `: “${t.naReason}”` : ""}. Your auditor will decide and tell you here.</p>}
      {(t.status === "accepted" || t.status === "resolved" || t.status === "na") && <p role="note" className="rounded-md border border-done-line bg-done-bg px-3 py-2 text-sm text-done">{t.status === "na" ? "Your auditor agreed this does not apply. Nothing more is needed." : "Your auditor has what they need. Thank you."}</p>}

      <LoadState state={msgs}>
        {(ms) => <Thread messages={ms} viewer="client" sendLabel="Send reply" placeholder="Write a reply to your auditor" disabledReason={open ? null : "This conversation is closed."}
          onSend={async (body) => { await ds.postClientMessage(t.engagementId, t.id, body); msgs.reload(); }} />}
      </LoadState>

      {open && (
        <section aria-label="Attach files" className="space-y-2">
          <label onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
            className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed border-line p-5 text-center text-sm hover:bg-sunken">
            <span className="font-medium">{t.kind === "request" ? "Add the documents" : "Attach a file"}</span>
            <span className="text-xs text-muted">Tap to choose files or take a photo · PDF, Excel, Word, CSV, images or ZIP · up to 100 MB each</span>
            <input ref={input} type="file" multiple accept={ACCEPT} className="sr-only" aria-label={`Attach files to “${t.subject}”`} onChange={(e) => void send(e.target.files)} />
          </label>
          {sending.length > 0 && (
            <ul aria-label="Files being sent" className="space-y-1.5">
              {sending.map((s) => (
                <li key={s.key} className="rounded border border-line px-2 py-1.5 text-sm">
                  <span className="flex items-center justify-between gap-2"><span className="min-w-0 break-words">{s.name}</span>
                    <span className="text-xs">{s.error ? <span className="text-blocked">Not sent</span> : s.done ? <span className="text-done">Received</span> : `${Math.round(s.progress * 100)}%`}</span></span>
                  {!s.error && !s.done && (
                    <span role="progressbar" aria-label={`Sending ${s.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(s.progress * 100)} className="mt-1 block h-1.5 overflow-hidden rounded bg-sunken">
                      <span className="block h-full bg-brand" style={{ width: `${Math.max(5, s.progress * 100)}%` }} />
                    </span>
                  )}
                  {s.error && <span role="alert" className="mt-1 block text-xs text-blocked">{s.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {mine.length > 0 && (
        <section aria-label="Your files">
          <h2 className="mb-1 text-sm font-semibold">Your files ({mine.length})</h2>
          <ul className="divide-y divide-line rounded border border-line text-sm">
            {mine.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5">
                <span className="min-w-0 basis-full break-words font-medium sm:flex-1 sm:basis-0">{f.name}</span>
                <StatusPill state={f.status} label={f.status === "pulled" ? "With your auditor" : f.status === "rejected" ? "Please send again" : "Received"} />
                <span className="text-xs text-muted">{fmtStamp(f.at)}</span>
                {f.status === "rejected" && f.note && <span className="basis-full text-xs text-blocked">{f.note}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {(t.status === "sent" || t.status === "returned") && (
        <section aria-label="Does not apply" className="rounded-md border border-line p-3">
          {!na ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="text-muted">Does this not apply to your organization?</span>
              <Button size="sm" onClick={() => setNa(true)}>It does not apply…</Button>
            </div>
          ) : (
            <form className="space-y-2" onSubmit={async (e) => {
              e.preventDefault();
              if (!naText.trim()) { setNaErr("Please say why, in a sentence or two."); return; }
              setNaBusy(true); setNaErr(undefined);
              try { await ds.postClientMessage(t.engagementId, t.id, naText.trim(), "na_request"); setNa(false); setNaText(""); reload(); }
              catch (x) { setNaErr((x as Error).message); } finally { setNaBusy(false); }
            }}>
              <label htmlFor="na-reason" className="block text-sm font-medium">Tell your auditor why it does not apply</label>
              <textarea id="na-reason" className={`${inputCls} min-h-[72px]`} value={naText} onChange={(e) => setNaText(e.target.value)} aria-describedby="na-hint" />
              <p id="na-hint" className="text-xs text-muted">Your auditor reads your reason and decides. You will see the decision here.</p>
              {naErr && <p role="alert" className="text-xs text-blocked">{naErr}</p>}
              <div className="flex justify-end gap-2"><Button onClick={() => { setNa(false); setNaErr(undefined); }}>Cancel</Button><Button kind="primary" type="submit" disabled={naBusy}>Send to your auditor</Button></div>
            </form>
          )}
        </section>
      )}
      <p className="text-xs text-muted">{plural(msgs.data?.length ?? 0, "message")} · your replies reach your audit team; they answer here.</p>
    </article>
  );
}

/** The Worker's and the portal's refusals, in words a client can act on. */
export function plainUploadError(name: string, message: string): string {
  const m = message.replace(/\.$/, "");
  if (/not open/i.test(m)) return `${name} was not sent: this item is no longer open for files. If you still need to send it, reply here.`;
  if (/sign in|session/i.test(m)) return `${name} was not sent: your session has ended. Please sign in again and retry.`;
  if (/larger|too large|size/i.test(m)) return `${name} was not sent: it is larger than the portal accepts (100 MB). Try splitting it, or send a ZIP.`;
  if (/empty/i.test(m)) return `${name} was not sent: the file is empty.`;
  return `${name} was not sent: ${m.charAt(0).toLowerCase()}${m.slice(1)}. Please try again.`;
}
